/**
 * Tab-Coordinated Session Manager
 *
 * Reduces resource waste by coordinating token refresh across browser tabs.
 * Only one tab acts as the "main" tab responsible for token refresh,
 * while other tabs listen for updates via BroadcastChannel.
 */

import { User } from 'firebase/auth';
import { decodeJWTPayload } from '../utils/tokenDecoder';
import { debugLog } from '../utils/debugLog';

interface SessionState {
  expiresAt: number;
  lastRefresh: number;
}

interface BroadcastMessage {
  type: 'TOKEN_REFRESHED' | 'TOKEN_EXPIRED' | 'MAIN_TAB_CLOSING' | 'HEARTBEAT' | 'LOGOUT';
  payload?: {
    token?: string;
    expiresAt?: number;
    tabId?: string;
  };
}

const SESSION_STATE_KEY = 'pithplay_session_state';
/** Refresh this long before the ID token expires. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;
/** Used when the token's expiry can't be read. */
const FALLBACK_REFRESH_MS = 50 * 60 * 1000;
/**
 * At most one immediate forced refresh per this window. With a client clock
 * far ahead of the server every fresh token already looks "about to expire",
 * and refreshing immediately would loop as fast as the network allows.
 */
const MIN_FORCED_REFRESH_INTERVAL_MS = 60 * 1000;

const safeRemoveItem = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage blocked (private mode, quota) - nothing to clear.
  }
};

export class TabCoordinatedSessionManager {
  private static instance: TabCoordinatedSessionManager;
  private broadcastChannel: BroadcastChannel | null;
  private tabId: string;
  private isMainTab: boolean = false;
  private refreshTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private user: User | null = null;
  private onTokenRefresh?: (token: string) => void;
  private onSessionExpired?: () => void;
  private onRemoteLogout?: () => void;
  /**
   * Bumped whenever pending scheduling work is invalidated (a newer schedule,
   * cleanup, logout). startTokenRefreshCycle awaits a token before it sets
   * its timer; a continuation from an older generation must not set one.
   */
  private cycleGeneration = 0;
  private refreshInFlight = false;
  private lastForcedRefreshAt = 0;
  /**
   * Set from the moment a logout starts (here or in another tab) until the
   * signed-out state is observed (cleanup). While set, nothing here calls
   * getIdToken(true): a forced refresh re-persists the user, and Firebase
   * then syncs that user back into every tab - including the one that just
   * logged out.
   */
  private logoutInProgress = false;

  private constructor() {
    this.tabId = this.generateTabId();
    this.broadcastChannel = new BroadcastChannel('pithplay_session');
    this.setupBroadcastListeners();
    this.electMainTab();
    this.startHeartbeat();

    // Cleanup on tab close
    window.addEventListener('beforeunload', () => this.cleanup(true));
    window.addEventListener('visibilitychange', () => this.handleVisibilityChange());
  }

  static getInstance(): TabCoordinatedSessionManager {
    if (!TabCoordinatedSessionManager.instance) {
      TabCoordinatedSessionManager.instance = new TabCoordinatedSessionManager();
    }
    return TabCoordinatedSessionManager.instance;
  }

  /**
   * The instance if one was created (AuthProvider creates it), without
   * creating one - logout paths must not start a manager of their own.
   */
  static current(): TabCoordinatedSessionManager | null {
    return TabCoordinatedSessionManager.instance ?? null;
  }

  /**
   * Initialize session management for a user.
   *
   * AuthContext calls this on EVERY ID-token change, including the change
   * this manager's own refresh causes, so it must be idempotent: it replaces
   * any pending refresh timer instead of adding another one. It used to add
   * one per call, and every refresh added two more (one here, one from
   * performTokenRefresh), so the timers - and forced refreshes - doubled
   * every hour a tab stayed open.
   */
  initializeSession(
    user: User,
    onTokenRefresh: (token: string) => void,
    onSessionExpired: () => void,
    onRemoteLogout?: () => void
  ) {
    this.user = user;
    this.onTokenRefresh = onTokenRefresh;
    this.onSessionExpired = onSessionExpired;
    this.onRemoteLogout = onRemoteLogout;

    if (!this.broadcastChannel) {
      this.broadcastChannel = new BroadcastChannel('pithplay_session');
      this.setupBroadcastListeners();
    }
    if (!this.heartbeatTimer) {
      this.electMainTab();
      this.startHeartbeat();
    }

    if (this.logoutInProgress) {
      debugLog(`Tab ${this.tabId}: Logout in progress, not scheduling a token refresh`);
      return;
    }

    if (this.isMainTab) {
      this.startTokenRefreshCycle();
    }
  }

  /**
   * Call before signing out. Stops this tab's refresh timer, makes sure no
   * forced refresh runs until the sign-out completes, and tells every other
   * tab to stop refreshing and sign out too.
   */
  beginLogout() {
    this.logoutInProgress = true;
    this.cancelRefresh();
    this.broadcastMessage({ type: 'LOGOUT', payload: { tabId: this.tabId } });
    safeRemoveItem(SESSION_STATE_KEY);
  }

  /** The sign-out failed and the user is still signed in: resume refreshing. */
  cancelLogout() {
    if (!this.logoutInProgress) return;
    this.logoutInProgress = false;
    if (this.user && this.isMainTab) {
      this.startTokenRefreshCycle();
    }
  }

  isLogoutInProgress(): boolean {
    return this.logoutInProgress;
  }

  /**
   * Cleanup session management. Called when the user is signed out (which
   * also ends any logout in progress) and, with closeChannel, on tab close.
   */
  cleanup(closeChannel = false) {
    if (this.isMainTab) {
      this.broadcastMessage({
        type: 'MAIN_TAB_CLOSING',
        payload: { tabId: this.tabId }
      });
      localStorage.removeItem('pithplay_main_tab');
    }

    this.clearTimers();
    this.cycleGeneration++;
    this.refreshInFlight = false;
    this.user = null;
    this.onTokenRefresh = undefined;
    this.onSessionExpired = undefined;
    this.onRemoteLogout = undefined;
    this.isMainTab = false;
    this.logoutInProgress = false;
    if (closeChannel && this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    } else {
      // Signed out: drop the shared session record. (Not on tab close - the
      // other tabs are still signed in.)
      safeRemoveItem(SESSION_STATE_KEY);
    }
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private electMainTab() {
    const storedMainTab = localStorage.getItem('pithplay_main_tab');
    const storedTimestamp = localStorage.getItem('pithplay_main_tab_timestamp');
    const now = Date.now();

    // If no main tab or main tab is stale (>30 seconds), become main tab
    if (!storedMainTab ||
        !storedTimestamp ||
        (now - parseInt(storedTimestamp)) > 30000) {

      this.becomeMainTab();
    } else {
      debugLog(`Tab ${this.tabId}: Acting as secondary tab`);
    }
  }

  private becomeMainTab() {
    const wasMainTab = this.isMainTab;
    this.isMainTab = true;
    localStorage.setItem('pithplay_main_tab', this.tabId);
    localStorage.setItem('pithplay_main_tab_timestamp', Date.now().toString());

    debugLog(`Tab ${this.tabId}: Elected as main tab`);

    // Re-elections of a tab that is already main (visibility changes, the
    // heartbeat check) must not start another cycle.
    if (this.user && !wasMainTab && !this.logoutInProgress) {
      this.startTokenRefreshCycle();
    }
  }

  private setupBroadcastListeners() {
    this.broadcastChannel?.addEventListener('message', (event: MessageEvent<BroadcastMessage>) => {
      const { type, payload } = event.data;

      switch (type) {
        case 'TOKEN_REFRESHED':
          if (payload?.token) {
            debugLog(`Tab ${this.tabId}: Received token refresh from main tab`);
            this.onTokenRefresh?.(payload.token);
            this.updateLocalSessionState(payload.expiresAt || 0);
          }
          break;

        case 'TOKEN_EXPIRED':
          debugLog(`Tab ${this.tabId}: Received session expired notification`);
          this.onSessionExpired?.();
          break;

        case 'MAIN_TAB_CLOSING':
          if (payload?.tabId !== this.tabId) {
            debugLog(`Tab ${this.tabId}: Main tab closing, attempting to become main`);
            setTimeout(() => this.electMainTab(), 100); // Brief delay to avoid race conditions
          }
          break;

        case 'HEARTBEAT':
          // Update main tab timestamp if we receive heartbeat
          if (payload?.tabId === localStorage.getItem('pithplay_main_tab')) {
            localStorage.setItem('pithplay_main_tab_timestamp', Date.now().toString());
          }
          break;

        case 'LOGOUT':
          if (payload?.tabId !== this.tabId) {
            this.handleRemoteLogout();
          }
          break;
      }
    });
  }

  /** Another tab logged out: stop refreshing here and sign this tab out too. */
  private handleRemoteLogout() {
    // Nothing to do in a tab that is already signed out - and setting the
    // flag there would block the next sign-in, since no signed-out event
    // would follow to clear it.
    if (!this.user || this.logoutInProgress) return;
    debugLog(`Tab ${this.tabId}: Another tab logged out, signing out here too`);
    this.logoutInProgress = true;
    this.cancelRefresh();
    this.onRemoteLogout?.();
  }

  private startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      if (this.isMainTab) {
        // Send heartbeat to indicate main tab is alive
        this.broadcastMessage({
          type: 'HEARTBEAT',
          payload: { tabId: this.tabId }
        });

        // Update timestamp
        localStorage.setItem('pithplay_main_tab_timestamp', Date.now().toString());
      } else {
        // Check if main tab is still alive
        const mainTabId = localStorage.getItem('pithplay_main_tab');
        const timestamp = localStorage.getItem('pithplay_main_tab_timestamp');

        if (mainTabId && timestamp) {
          const timeSinceHeartbeat = Date.now() - parseInt(timestamp);

          // If no heartbeat for 45 seconds, attempt to become main tab
          if (timeSinceHeartbeat > 45000) {
            debugLog(`Tab ${this.tabId}: Main tab appears dead, taking over`);
            this.becomeMainTab();
          }
        }
      }
    }, 15000); // Check every 15 seconds
  }

  /** Drops the pending refresh and invalidates any schedule still being computed. */
  private cancelRefresh() {
    this.cycleGeneration++;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** The only place a refresh timer is set: always replaces the previous one. */
  private scheduleRefresh(delayMs: number, generation: number) {
    if (generation !== this.cycleGeneration) return;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.performTokenRefresh();
    }, delayMs);
  }

  private async startTokenRefreshCycle() {
    if (!this.user || !this.isMainTab || this.logoutInProgress) return;

    // Supersede any earlier schedule, including one still awaiting its token.
    this.cancelRefresh();
    const generation = this.cycleGeneration;
    const user = this.user;

    try {
      // Get current token to determine refresh timing
      const token = await user.getIdToken(false);
      if (generation !== this.cycleGeneration) return;

      if (token) {
        const payload = decodeJWTPayload(token);
        if (!payload) {
          console.error('Failed to decode JWT token payload');
          this.scheduleRefresh(FALLBACK_REFRESH_MS, generation);
          return;
        }
        const expiryTime = payload.exp * 1000;
        const currentTime = Date.now();

        // Schedule refresh 5 minutes before expiry
        const refreshTime = expiryTime - currentTime - REFRESH_LEAD_MS;
        this.updateLocalSessionState(expiryTime);

        if (refreshTime > 0) {
          debugLog(`Main tab ${this.tabId}: Token expires at ${new Date(expiryTime).toLocaleTimeString()}, scheduling refresh in ${Math.round(refreshTime / 60000)} minutes`);
          this.scheduleRefresh(refreshTime, generation);
        } else if (currentTime - this.lastForcedRefreshAt < MIN_FORCED_REFRESH_INTERVAL_MS) {
          // Just refreshed and the new token still looks nearly expired (a
          // skewed clock): try again later instead of looping.
          this.scheduleRefresh(MIN_FORCED_REFRESH_INTERVAL_MS, generation);
        } else {
          // Token expired or expires soon, refresh immediately
          await this.performTokenRefresh();
        }
      }
    } catch (error) {
      console.error(`Main tab ${this.tabId}: Error starting token refresh cycle:`, error);

      // Fall back to 50-minute refresh cycle
      this.scheduleRefresh(FALLBACK_REFRESH_MS, generation);
    }
  }

  private async performTokenRefresh() {
    if (!this.user || !this.isMainTab || this.logoutInProgress) return;
    // getIdToken(true) re-enters initializeSession through AuthContext's
    // token listener; one refresh at a time.
    if (this.refreshInFlight) return;

    const user = this.user;
    this.refreshInFlight = true;
    this.lastForcedRefreshAt = Date.now();
    try {
      debugLog(`Main tab ${this.tabId}: Refreshing token...`);

      const newToken = await user.getIdToken(true);
      // Signed out (or logging out) while the request was in flight.
      if (this.user !== user || this.logoutInProgress) return;

      if (newToken) {
        const payload = decodeJWTPayload(newToken);
        if (!payload) {
          console.error('Failed to decode refreshed JWT token payload');
          return;
        }
        const expiryTime = payload.exp * 1000;

        // Broadcast to all tabs
        this.broadcastMessage({
          type: 'TOKEN_REFRESHED',
          payload: {
            token: newToken,
            expiresAt: expiryTime
          }
        });

        // Update local state
        this.updateLocalSessionState(expiryTime);

        debugLog(`Main tab ${this.tabId}: Token refreshed successfully, broadcasted to all tabs`);
      }
    } catch (error) {
      if (this.user !== user || this.logoutInProgress) return;
      console.error(`Main tab ${this.tabId}: Token refresh failed:`, error);

      // Broadcast session expiry
      this.broadcastMessage({
        type: 'TOKEN_EXPIRED'
      });

      this.onSessionExpired?.();
      return;
    } finally {
      this.refreshInFlight = false;
    }

    // Schedule next refresh (replaces whatever the token listener scheduled
    // while the refresh was in flight).
    this.startTokenRefreshCycle();
  }

  private broadcastMessage(message: BroadcastMessage) {
    if (!this.broadcastChannel) return;
    try {
      this.broadcastChannel.postMessage(message);
    } catch (error) {
      console.error('Failed to broadcast message:', error);
    }
  }

  // Timing only. The ID token itself used to be written here too, and stayed
  // in localStorage after logout.
  private updateLocalSessionState(expiresAt: number) {
    const sessionState: SessionState = {
      expiresAt,
      lastRefresh: Date.now()
    };

    try {
      localStorage.setItem(SESSION_STATE_KEY, JSON.stringify(sessionState));
    } catch {
      // Storage blocked - this record is diagnostic only.
    }
  }

  private clearTimers() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'visible' && !this.isMainTab) {
      // Tab became visible, check if we should become main tab
      setTimeout(() => this.electMainTab(), 1000);
    }
  }

  /**
   * Get current session statistics for debugging
   */
  getSessionStats() {
    return {
      tabId: this.tabId,
      isMainTab: this.isMainTab,
      hasPendingRefresh: this.refreshTimer !== null,
      sessionState: JSON.parse(localStorage.getItem(SESSION_STATE_KEY) || '{}'),
      mainTabId: localStorage.getItem('pithplay_main_tab'),
      lastHeartbeat: localStorage.getItem('pithplay_main_tab_timestamp')
    };
  }
}
